/**
 * The app: sign-in gate, four screens, and the sync that runs beside them.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

import { Actions, type Undoable } from '../actions';
import { ApiClient } from '../api/client';
import { browserSession } from '../api/session';
import type { Food } from '../api/types';
import { Cognito } from '../auth/cognito';
import { API_BASE_URL, COGNITO } from '../config';
import { outstanding } from '../domain/shopping';
import { noteAddButton } from '../pwa/diagnostics';
import { whenOpened } from '../pwa/foreground';
import { openDatabase, type Database } from '../store/database';
import { SyncEngine } from '../sync/engine';
import { Groceries } from './groceries';
import { Inventory } from './inventory';
import { InstallInvitation } from './install';
import { QuickAdd } from './quick-add';
import { RemindersInvitation } from './reminders';
import { Settings } from './settings';
import { Shopping } from './shopping';
import { SignIn } from './sign-in';
import { useHousehold } from './store';

type Screen = 'inventory' | 'shopping' | 'groceries' | 'settings';

/** Long enough to reach for, short enough that an offer nobody took stops claiming the corner. */
const UNDO_LIFETIME_MS = 5_000;

export function App() {
  const session = useMemo(() => browserSession(), []);
  const cognito = useMemo(() => new Cognito(COGNITO, session), [session]);
  const client = useMemo(() => new ApiClient(API_BASE_URL, cognito), [cognito]);

  const [db, setDb] = useState<Database | null>(null);
  const [signedIn, setSignedIn] = useState(cognito.isSignedIn);
  const [screen, setScreen] = useState<Screen>('inventory');
  const [adding, setAdding] = useState(false);
  const [catalog, setCatalog] = useState<Food[]>([]);
  const [undoable, setUndoable] = useState<Undoable | null>(null);
  const [offline, setOffline] = useState(false);

  const [view, reload] = useHousehold(db);

  useEffect(() => {
    void openDatabase().then(setDb);
  }, []);

  const actions = useMemo(
    () =>
      db === null
        ? null
        : new Actions(db, {
            catalog: () => catalog,
            locations: () => view.locations,
            lookUp: (name) => client.lookUpFood(name),
          }),
    [db, client, catalog, view.locations],
  );

  const sync = useCallback(async () => {
    if (db === null || !signedIn) {
      return;
    }

    try {
      await new SyncEngine(db, client).run();
      setOffline(false);
    } catch {
      // Offline is the ordinary case here, not a failure worth interrupting anyone over. The
      // queue keeps the work and the next attempt sends it.
      setOffline(true);
    }
    await reload();
  }, [db, client, signedIn, reload]);

  useEffect(() => {
    void sync();
  }, [sync]);

  // An app that is resumed rather than reloaded would otherwise show whatever it was showing
  // when it was put down, which for a household where somebody else has been shopping is the
  // stale inventory this whole design exists to avoid.
  useEffect(() => whenOpened(() => void sync()), [sync]);

  // An undo offer is about the gesture that just happened. Left up it becomes an invitation to
  // reverse something the user has stopped thinking about, which is its own kind of stale data.
  useEffect(() => {
    if (undoable === null) {
      return undefined;
    }
    const lapse = setTimeout(() => setUndoable(null), UNDO_LIFETIME_MS);
    return () => clearTimeout(lapse);
  }, [undoable]);

  // The catalog is small, changes rarely, and is what fills in a grocery entry the first time
  // a food is named — where it usually lives and how long it usually keeps. Failing to fetch it
  // is survivable: a new entry then arrives blank rather than guessed, and is still editable.
  useEffect(() => {
    if (!signedIn) {
      return;
    }
    void client
      .foods()
      .then(setCatalog)
      .catch(() => undefined);
  }, [client, signedIn]);

  if (!signedIn) {
    return (
      <SignIn
        cognito={cognito}
        onSignedIn={() => {
          setSignedIn(true);
        }}
      />
    );
  }

  if (db === null || actions === null) {
    return <p class="empty">Opening…</p>;
  }

  const changed = (undo: Undoable | null): void => {
    setUndoable(undo);
    void reload();
  };

  // The offer belongs to the row the gesture happened on, and that row is no longer on screen.
  const show = (next: Screen): void => {
    setScreen(next);
    setUndoable(null);
  };

  return (
    <div class="app">
      <header class="bar">
        <nav>
          <button
            type="button"
            class={screen === 'inventory' ? 'on' : ''}
            onClick={() => show('inventory')}
          >
            Kitchen
          </button>
          <button
            type="button"
            class={screen === 'shopping' ? 'on' : ''}
            onClick={() => show('shopping')}
          >
            Shopping
            {outstanding(view.shopping).length > 0 ? (
              <span class="count">{outstanding(view.shopping).length}</span>
            ) : null}
          </button>
          <button
            type="button"
            class={screen === 'groceries' ? 'on' : ''}
            onClick={() => show('groceries')}
          >
            Groceries
          </button>
          <button
            type="button"
            class={screen === 'settings' ? 'on' : ''}
            onClick={() => show('settings')}
          >
            Settings
          </button>
        </nav>
        <Status offline={offline} queued={view.queued} onRetry={() => void sync()} />
      </header>

      <main>
        {/* Both of these ask for something. Settings is where the household came to decide,
            so it is the one screen that should not be interrupted with another question. */}
        {screen === 'settings' ? null : (
          <>
            <InstallInvitation />
            <RemindersInvitation client={client} />
          </>
        )}

        {screen === 'inventory' ? (
          <Inventory items={view.items} actions={actions} onChanged={changed} />
        ) : screen === 'shopping' ? (
          <Shopping
            shopping={view.shopping}
            actions={actions}
            onChanged={() => void reload()}
          />
        ) : screen === 'groceries' ? (
          <Groceries
            groceries={view.groceries}
            locations={view.locations}
            actions={actions}
            onChanged={changed}
          />
        ) : (
          <Settings
            client={client}
            cognito={cognito}
            onSignedOut={() => {
              setSignedIn(false);
              setScreen('inventory');
            }}
          />
        )}
      </main>

      {/* The sheet covers this, but covered is not the same as gone: left rendered it stays in
          the tab order behind a modal that is meant to be the only thing reachable. */}
      {screen === 'inventory' && !adding ? (
        <button
          type="button"
          class="add"
          ref={noteAddButton}
          onClick={() => setAdding(true)}
          aria-label="Add"
        >
          +
        </button>
      ) : null}

      {adding ? (
        <QuickAdd
          groceries={view.groceries}
          items={view.items}
          locations={view.locations}
          actions={actions}
          onAdded={() => void reload()}
          onClose={() => setAdding(false)}
        />
      ) : null}

      {undoable === null ? null : (
        <div class="undo" role="status">
          <span>{undoable.description}</span>
          <button
            type="button"
            onClick={() => {
              void undoable.undo().then(() => {
                setUndoable(null);
                void reload();
              });
            }}
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Only being offline is worth saying. A queue draining against a reachable server is
 * housekeeping the user did not ask about and cannot act on, and a badge that blinks on
 * every write teaches people to read the header instead of the kitchen.
 */
function Status({
  offline,
  queued,
  onRetry,
}: {
  offline: boolean;
  queued: number;
  onRetry: () => void;
}) {
  if (!offline) {
    return null;
  }

  return (
    <button type="button" class="status" onClick={onRetry}>
      Offline
      {queued > 0 ? ` · ${queued}` : ''}
    </button>
  );
}
